import React, { useEffect, useRef, useState } from "react";
import type { ImageUploadItem } from "antd-mobile/es/components/image-uploader";
import { PreviewImagesRef } from "../PreviewImages";
import MessageBox from "../message-box";
import {
  AttachmentType,
  AttachmentItem,
  uploadFile,
  listByFileIds,
  ATTACHMENT_MAX,
  ATTACHMENT_MAX_SIZE,
  IMAGE_MAX_SIZE,
  ALLOWED_IMAGE_EXT,
  ALLOWED_VIDEO_EXT,
} from "./service";
import styles from "./index.module.scss";

// 在 ImageUploadItem 上附带类型、fileId 与缩略图
export type UploadItem = ImageUploadItem & {
  extra?: { type: AttachmentType; fileId: string; thumbUrl?: string };
};

export const isVideoItem = (item?: ImageUploadItem) =>
  (item as UploadItem)?.extra?.type === "video";

// 判断两个 fileId 列表是否为同一集合（忽略顺序），用于受控同步时避免无谓重建
const sameIdSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

export interface UseAttachmentUploadParams {
  /** 已选附件的 fileId 列表（回显/受控） */
  value?: string[];
  /** 变更时回调，抛出最新 fileId 列表 */
  onChange?: (fileIds: string[]) => void;
  /** 最大数量，默认 9 */
  maxCount?: number;
  /** 单个附件最大字节数，默认 50M */
  maxSize?: number;
  /** 图片单独最大字节数，默认 20M */
  imageMaxSize?: number;
  /** 允许的扩展名白名单（小写、不含点） */
  allowedExts?: string[];
}

/**
 * 附件上传逻辑 hook（复用 service.ts）。
 * 供网格式组件 <AttachmentUpload>（ImageUploader 内置上传）与
 * 文本框内嵌上传（外部胶囊按钮 + 隐藏 input，走 handleFilesSelected）共用，
 * 统一 items 状态 / value 回显 / 校验 / 上传 / 预览 / 视频封面渲染。
 */
export function useAttachmentUpload(params: UseAttachmentUploadParams) {
  const {
    value,
    onChange,
    maxCount = ATTACHMENT_MAX,
    maxSize = ATTACHMENT_MAX_SIZE,
    imageMaxSize = IMAGE_MAX_SIZE,
    allowedExts = [...ALLOWED_IMAGE_EXT, ...ALLOWED_VIDEO_EXT],
  } = params;
  const extHint = allowedExts.join("/");
  const [items, setItems] = useState<UploadItem[]>([]);
  // 最新 items 引用：供异步上传回调拼接，避免闭包快照导致的并发覆盖丢失
  const itemsRef = useRef<UploadItem[]>([]);
  // 上传中（await 期间尚未 emit）的文件数占位，计算剩余额度时一并扣除，
  // 防止多批次快速并发时各自只按已完成数校验、合并后突破 maxCount
  const pendingRef = useRef(0);
  // 组件挂载标记：异步回调 setState 前判断，避免卸载后 setState 警告
  const mountedRef = useRef(true);
  const previewRef = useRef<PreviewImagesRef>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoVisible, setVideoVisible] = useState(false);
  // 上传进行中：供文本框禁用上传入口，从源头避免并发再次选择
  const [uploading, setUploading] = useState(false);
  // 受控同步请求序号：value 快速连续变化时，异步 listByFileIds 可能乱序返回，
  // 只接受最新一次同步请求的结果，丢弃过期响应，避免旧集合覆盖新集合
  const syncReqRef = useRef(0);

  // 受控同步：按 fileId 集合对齐 value 与本地 items。
  // - 集合一致（含本地上传后 onChange 回灌的同集合）→ 跳过，避免自更新回环
  // - value 为空 → 清空本地（订单切换/重置场景，不再残留旧附件）
  // - 集合不一致 → 以 value 为准拉取详情整体重建（回填/切换新 fileId 场景）
  useEffect(() => {
    const nextIds = value || [];
    const currentIds = items.map(
      (i) => i.extra?.fileId || String(i.key || "")
    );
    if (sameIdSet(nextIds, currentIds)) return;
    // 每次真正需要同步都递增序号；清空场景也递增，令之前 in-flight 的请求失效
    const reqId = ++syncReqRef.current;
    if (nextIds.length === 0) {
      setItems([]);
      return;
    }
    listByFileIds(nextIds)
      .then((list) => {
        if (!mountedRef.current) return;
        // 已有更新的同步请求发起 → 丢弃本次过期结果，避免乱序覆盖
        if (reqId !== syncReqRef.current) return;
        setItems(
          list.map((it) => ({
            key: it.fileId,
            url: it.url,
            thumbnailUrl: it.type === "image" ? it.thumbUrl || it.url : "",
            extra: { type: it.type, fileId: it.fileId, thumbUrl: it.thumbUrl },
          }))
        );
      })
      .catch((e) => {
        console.error("AttachmentUpload listByFileIds failed", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 同步最新 items 到 ref，供异步上传回调读取（防并发覆盖）
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // 卸载时标记，供异步回调跳过 setState（避免 unmounted setState 警告）
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const emit = (next: UploadItem[]) => {
    if (!mountedRef.current) return;
    // 本地变更（上传/删除）优先：作废之前 in-flight 的回填请求，
    // 避免回填结果晚到覆盖用户刚操作的列表
    syncReqRef.current++;
    setItems(next);
    onChange?.(next.map((i) => i.extra?.fileId || String(i.key || "")));
  };

  // 单文件校验（纯判定，不弹提示）：'ok' | 'type'(格式不符) | 'size'(超大小)
  const validateFile = (file: File): "ok" | "type" | "size" => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!allowedExts.includes(ext)) return "type";
    const isImage = ALLOWED_IMAGE_EXT.includes(ext);
    const limit = isImage ? Math.min(imageMaxSize, maxSize) : maxSize;
    if (file.size > limit) return "size";
    return "ok";
  };

  // 由后端附件项构建 UploadItem
  const buildUploadItem = (item: AttachmentItem): UploadItem => ({
    key: item.fileId,
    url: item.url,
    thumbnailUrl: item.type === "image" ? item.thumbUrl || item.url : "",
    extra: { type: item.type, fileId: item.fileId, thumbUrl: item.thumbUrl },
  });

  // 格式白名单 + 单个大小校验（网格模式 ImageUploader 用：不通过则拒绝该文件并逐个提示）
  const beforeUpload = (file: File): File | null => {
    const r = validateFile(file);
    if (r === "type") {
      MessageBox.toast(`仅支持 ${extHint} 格式`);
      return null;
    }
    if (r === "size") {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const isImage = ALLOWED_IMAGE_EXT.includes(ext);
      const limit = isImage ? Math.min(imageMaxSize, maxSize) : maxSize;
      const limitMB = Math.round(limit / 1024 / 1024);
      MessageBox.toast(`${isImage ? "图片" : "单个附件"}不能超过 ${limitMB}M`);
      return null;
    }
    return file;
  };

  // 上传单个文件 → UploadItem（供 ImageUploader 的 upload 使用，失败弹提示）
  const doUpload = async (file: File): Promise<UploadItem> => {
    try {
      return buildUploadItem(await uploadFile(file));
    } catch (e) {
      MessageBox.toast("上传失败，请重试");
      throw e;
    }
  };

  // 外部 input 选择文件后触发（文本框胶囊按钮用）：全宽容 / 部分成功——
  // 能传的都传（成功靠缩略图体现，不提示）；数量超限 / 格式 / 大小 / 上传失败的
  // 合并成「最多一条」提示。单张时给精确原因（复用 beforeUpload 逐项文案）。
  // 上传一批文件：占位 pending + uploading、依次上传、finally 复位、成功合并进列表（二次裁剪防超限）。
  // 返回成功项与失败数，供调用方决定提示。
  const runUpload = async (
    files: File[]
  ): Promise<{ uploaded: UploadItem[]; failCount: number }> => {
    pendingRef.current += files.length;
    setUploading(true);
    const uploaded: UploadItem[] = [];
    let failCount = 0;
    try {
      for (const f of files) {
        try {
          // eslint-disable-next-line no-await-in-loop
          uploaded.push(buildUploadItem(await uploadFile(f)));
        } catch {
          failCount += 1;
        }
      }
    } finally {
      pendingRef.current -= files.length;
      if (mountedRef.current) setUploading(pendingRef.current > 0);
    }
    if (uploaded.length) {
      // 二次裁剪防御：即便发生极端并发，也保证合并后不超过 maxCount
      emit([...itemsRef.current, ...uploaded].slice(0, maxCount));
    }
    return { uploaded, failCount };
  };

  // 外部 input 选择文件后触发（文本框胶囊按钮用）：全宽容 / 部分成功——
  // 能传的都传（成功靠缩略图体现，不提示）；数量超限 / 格式 / 大小 / 上传失败的
  // 合并成「最多一条」提示。单张时给精确原因（复用 beforeUpload 逐项文案）。
  const handleFilesSelected = async (files: FileList | File[]) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;

    // 剩余额度 = 上限 - 已完成 - 上传中(pending)
    const room = maxCount - itemsRef.current.length - pendingRef.current;
    if (room <= 0) {
      MessageBox.toast(`最多上传 ${maxCount} 个`);
      return;
    }

    // 单张：精确校验与提示（格式/大小不符由 beforeUpload 内 toast）
    if (arr.length === 1) {
      const file = beforeUpload(arr[0]);
      if (!file) return;
      const { failCount } = await runUpload([file]);
      if (failCount) MessageBox.toast("上传失败，请重试");
      return;
    }

    // 多张：数量超限截断；格式/大小逐个分类（不合规不阻断其余合规文件）
    const picked = arr.slice(0, room);
    const overCount = arr.length - picked.length;
    let typeCount = 0;
    let sizeCount = 0;
    const valid: File[] = [];
    picked.forEach((f) => {
      const r = validateFile(f);
      if (r === "type") typeCount += 1;
      else if (r === "size") sizeCount += 1;
      else valid.push(f);
    });

    const { uploaded, failCount } = valid.length
      ? await runUpload(valid)
      : { uploaded: [], failCount: 0 };

    // 未添加的（格式/大小/超数量/上传失败）合并成一条提示，成功不提示
    const notAdded = typeCount + sizeCount + overCount + failCount;
    if (notAdded > 0) {
      const prefix = uploaded.length > 0 ? "部分文件未添加" : "文件未添加";
      MessageBox.toast(`${prefix}（${notAdded} 个），请检查大小、格式或数量`);
    }
  };

  // 预览分发：视频 → VideoPlayer；图片 → PreviewImages
  const handlePreview = (_index: number, item: ImageUploadItem) => {
    const cur = item as UploadItem;
    if (cur.extra?.type === "video") {
      setVideoUrl(cur.url);
      setVideoVisible(true);
      return;
    }
    const images = items.filter((i) => i.extra?.type !== "video");
    const imgIndex = images.findIndex((i) => i.key === cur.key);
    previewRef.current?.open({
      index: imgIndex < 0 ? 0 : imgIndex,
      urls: images.map((i) => i.url),
    });
  };

  // 视频封面自绘（复用于网格与文本框缩略图）：<img> 带 no-referrer 才能加载 OSS 截帧图
  const renderItem = (originNode: React.ReactNode, file: ImageUploadItem) => {
    if (!isVideoItem(file)) return originNode;
    const cur = file as UploadItem;
    const thumb = cur.extra?.thumbUrl;
    return (
      <div className={styles.videoCell} key={String(cur.key)}>
        {originNode}
        {thumb ? (
          <img
            className={styles.videoCover}
            src={thumb}
            alt=""
            referrerPolicy="no-referrer"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <video
            className={styles.videoCover}
            src={`${cur.url}#t=0.1`}
            muted
            preload="metadata"
            playsInline
            ref={(el) => {
              if (el) {
                el.setAttribute("webkit-playsinline", "true");
                el.setAttribute("x5-playsinline", "true");
              }
            }}
          />
        )}
        <span
          className={styles.playIcon}
          onClick={() => {
            setVideoUrl(cur.url);
            setVideoVisible(true);
          }}
        />
      </div>
    );
  };

  return {
    items,
    emit,
    beforeUpload,
    doUpload,
    handleFilesSelected,
    handlePreview,
    renderItem,
    previewRef,
    videoUrl,
    videoVisible,
    setVideoVisible,
    uploading,
    maxCount,
  };
}
